import { PRESCRIPTION_ROUTE, routeForForm, type PrescriptionItemInput, type PrescriptionRoute, type RxSetItemInput } from '@his/shared';

export interface PrescriptionDraft {
  productId: string | null;
  drugName: string;
  strength: string;
  form: string;
  dose: string;
  route: PrescriptionRoute | '';
  frequency: string;
  durationDays: number;
  quantity: number;
  instructions: string;
  isSos: boolean;
}

export const ROUTE_LABELS: Record<PrescriptionRoute, string> = {
  oral: 'Oral (by mouth)',
  sublingual: 'Sublingual (under the tongue)',
  buccal: 'Buccal (inside the cheek)',
  iv: 'Intravenous (IV)',
  im: 'Intramuscular (IM)',
  sc: 'Subcutaneous (SC)',
  intradermal: 'Intradermal',
  topical: 'Topical (on the skin)',
  transdermal: 'Transdermal (patch)',
  inhalation: 'Inhalation',
  rectal: 'Rectal',
  vaginal: 'Vaginal',
  ophthalmic: 'Eye',
  otic: 'Ear',
  nasal: 'Nasal',
  other: 'Other',
};

export const ROUTE_OPTIONS = PRESCRIPTION_ROUTE.map((route) => ({ value: route, label: ROUTE_LABELS[route] }));

export function emptyDraft(): PrescriptionDraft {
  return {
    productId: null,
    drugName: '',
    strength: '',
    form: '',
    dose: '',
    route: '',
    frequency: '',
    durationDays: 0,
    quantity: 0,
    instructions: '',
    isSos: false,
  };
}

export function draftFromSetItem(item: Partial<RxSetItemInput>): PrescriptionDraft {
  return {
    ...emptyDraft(),
    productId: item.productId ?? null,
    drugName: item.drugName ?? '',
    strength: item.strength ?? '',
    form: item.form ?? '',
    dose: item.dose ?? '',
    route: item.route ?? '',
    frequency: item.frequency ?? '',
    durationDays: item.durationDays ?? 0,
    quantity: item.quantity ?? 0,
    instructions: item.instructions ?? '',
    isSos: item.isSos ?? false,
  };
}

export function withForm(draft: PrescriptionDraft, form: string): PrescriptionDraft {
  const suggested = draft.route ? null : routeForForm(form);
  return { ...draft, form, ...(suggested ? { route: suggested } : {}) };
}

export function isNamed(draft: PrescriptionDraft): boolean {
  return draft.drugName.trim().length > 2;
}

export function missingRoute(drafts: readonly PrescriptionDraft[]): string[] {
  return drafts.filter((draft) => isNamed(draft) && !draft.route).map((draft) => draft.drugName.trim());
}

export function toPrescriptionItem(draft: PrescriptionDraft): PrescriptionItemInput {
  if (!draft.route) throw new Error(`Choose the route for ${draft.drugName.trim() || 'this medicine'}`);
  return {
    productId: draft.productId,
    drugName: draft.drugName.trim(),
    strength: draft.strength.trim() || undefined,
    form: draft.form.trim() || undefined,
    dose: draft.dose.trim(),
    route: draft.route,
    frequency: draft.frequency.trim(),
    durationDays: draft.durationDays,
    quantity: draft.quantity,
    instructions: draft.instructions.trim() || undefined,
    isSos: draft.isSos,
  };
}
